import { getTimeDateString } from '../../../utility/date';
import {
  assignAtPath,
  coerceQueryParamValue,
  decodeParamValue,
  parseQueryParamKey,
} from '../../../utility/urlQuery';

const addLeadInterestsPayload = (data) => {
  const { selectedListings, leadId } = data || {};
  return {
    interests: selectedListings?.map((itemId) => {
      return {
        lead_id: leadId,
        listing_id: itemId,
      };
    }),
  };
};

const updateLeadNamePayload = (data) => {
  const { leadName } = data || {};
  return {
    lead: {
      name: leadName,
    },
  };
};
const addTaskPayload = (data) => {
  const { values, id } = data || {};
  const task = {
    image_ids: values.attachments.map((img) => img.id),
    task_type_id: values.completedTaskType,
    notes: values.notes,
    task_purpose_id: values.completedsubTaskType,
    completed_at: getTimeDateString(values.completionDate, false, true),
    due_date: getTimeDateString(values.dueDate, false, true),
    taskable_id: id,
    taskable_type: 'Lead',
    listing_id: values?.taskInterest?.id,
  };
  // Only attach the planned (next) task when the user actually started it (a planned Task
  // Type is selected). When the planned task is optional and left empty, omit
  // child_attributes entirely instead of sending a block of nulls.
  if (values.plannedTaskType) {
    task.child_attributes = {
      task_type_id: values.plannedTaskType,
      task_purpose_id: values.plannedsubTaskType,
      due_date: getTimeDateString(values.dueDate, false, true),
      taskable_id: id,
      taskable_type: 'Lead',
      listing_id: values?.taskInterest?.id,
    };
  }
  return { task };
};
const addLeadPayload = (data) => {
  const { values, user } = data || {};

  return {
    lead: {
      user_id: user?.id,
      agency_id: user?.agency?.id,
      lead_user_name: values?.name,
      lead_email_address: values?.email,
      lead_phone_number: values?.phone,
      lead_whatsapp_number: values?.whatsapp,
      listing_id: values?.interest?.id,
    },
  };
};

/**
 * TruLeads Export (86eyzcrcf) — POST /api/surge/lms/leads/export.
 *
 * The URL carries bare filter keys (`location_id`), while the API expects the nested f / s / sort
 * shape. Each filter already declares that mapping as its `queryParamKey` (`f[location_id][]`), so
 * the body is derived from the filter definitions rather than from a second hand-written key list —
 * a duplicate list is what silently drifts when a filter is added or renamed.
 *
 * The key-shape rules themselves live with the helpers in utility/urlQuery.
 */
const buildTruLeadsExportPayload = ({ queryObj = {}, filtersList = [], activeTab, userId, email } = {}) => {
  const body = {};

  const applyFilter = (item) => {
    const rawValue = queryObj?.[item?.key];
    if (rawValue === undefined || rawValue === null || rawValue === '') return;

    // A unified date range is declared against the BI endpoint's flat start_date / end_date, which
    // the export does not accept; the item names the export's own keys instead when they differ.
    const queryParamKey = item?.exportQueryParamKey || item?.queryParamKey;
    const values = decodeParamValue(rawValue).split(',');

    if (Array.isArray(queryParamKey)) {
      // A range: one key per end, in order.
      queryParamKey.forEach((key, index) => {
        const parsed = parseQueryParamKey(key);
        const value = values[index];
        if (!parsed || value === undefined || value === '') return;
        // Dates stay strings — coercing would turn 2026-09-01 into NaN.
        assignAtPath((body[parsed.root] = body[parsed.root] || {}), parsed.path, value);
      });
      return;
    }

    const parsed = parseQueryParamKey(queryParamKey);
    if (!parsed) return;
    // Free-text search stays a string: a lead named "12345" is not the number 12345.
    const coerce = parsed.root === 's' ? (v) => v : coerceQueryParamValue;
    const value = parsed.isArray ? values.map(coerce) : coerce(values.length > 1 ? values.join(',') : values[0]);
    assignAtPath((body[parsed.root] = body[parsed.root] || {}), parsed.path, value);
  };

  filtersList.forEach((item) => {
    if (item?.subList?.length) {
      item.subList.forEach(applyFilter);
      return;
    }
    applyFilter(item);
  });

  // The tab is a UI param, not a filter, and selects its own boolean flag on the API.
  if (activeTab === 'bayut_match') {
    body.f = { ...body.f, bayut_match: true };
  }

  // Agency staff are scoped to their own leads by the user filter the table already applies.
  if (userId !== undefined && userId !== null && body?.f?.user_id === undefined) {
    body.f = { ...body.f, user_id: coerceQueryParamValue(`${userId}`) };
  }

  if (email) body.email = email;

  return body;
};

export default {
  addLeadInterestsPayload,
  addTaskPayload,
  updateLeadNamePayload,
  addLeadPayload,
  buildTruLeadsExportPayload,
};
